﻿import type {
  ProcessedImage,
  WorkerProcessRequest,
  WorkerRequest,
  WorkerResponse,
  WorkerRotateRequest
} from '../types/catalog'

type WorkerRequestWithoutId = Omit<WorkerProcessRequest, 'id'> | Omit<WorkerRotateRequest, 'id'>

const THUMB_MAX = 480
const PREVIEW_MAX = 1600

let worker: Worker | null = null
const pending = new Map<string, { resolve: (value: ProcessedImage) => void; reject: (reason?: unknown) => void }>()

function canUseWorker(): boolean {
  return typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap !== 'undefined'
}

function getWorker(): Worker {
  if (worker) return worker

  worker = new Worker(new URL('../workers/image.worker.ts', import.meta.url), { type: 'module' })
  worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
    const result = event.data
    const handler = pending.get(result.id)
    if (!handler) return
    pending.delete(result.id)
    if (result.ok) handler.resolve(result.payload)
    else handler.reject(new Error(result.error))
  })
  worker.addEventListener('error', (event) => {
    const error = new Error(event.message || 'Image worker unavailable')
    for (const handler of pending.values()) handler.reject(error)
    pending.clear()
    worker?.terminate()
    worker = null
  })

  return worker
}

function runWorker(request: WorkerRequestWithoutId): Promise<ProcessedImage> {
  const id = crypto.randomUUID()
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject })
    getWorker().postMessage({ ...request, id } satisfies WorkerRequest)
  })
}

function outputSize(width: number, height: number, maxDimension: number): [number, number] {
  const largest = Math.max(width, height)
  if (largest <= maxDimension) return [width, height]
  const ratio = maxDimension / largest
  return [Math.max(1, Math.round(width * ratio)), Math.max(1, Math.round(height * ratio))]
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Image conversion failed')), type, quality)
  })
}

async function renderOnMainThread(
  bitmap: ImageBitmap,
  maxDimension: number,
  type: string,
  quality: number
): Promise<Blob> {
  const [width, height] = outputSize(bitmap.width, bitmap.height, maxDimension)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { alpha: type !== 'image/jpeg' })
  if (!context) throw new Error('Canvas 2D unavailable')
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(bitmap, 0, 0, width, height)
  return canvasToBlob(canvas, type, quality)
}

async function processOnMainThread(file: Blob): Promise<ProcessedImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  try {
    const [preview, thumbnail] = await Promise.all([
      renderOnMainThread(bitmap, PREVIEW_MAX, 'image/webp', 0.86),
      renderOnMainThread(bitmap, THUMB_MAX, 'image/webp', 0.76)
    ])
    return {
      original: file,
      preview,
      thumbnail,
      width: bitmap.width,
      height: bitmap.height,
      mimeType: file.type || 'application/octet-stream',
      size: file.size
    }
  } finally {
    bitmap.close()
  }
}

async function rotateOnMainThread(
  file: Blob,
  degrees: 90 | -90 | 180,
  preferredMimeType: string
): Promise<ProcessedImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  const swapSides = Math.abs(degrees) === 90
  const canvas = document.createElement('canvas')
  canvas.width = swapSides ? bitmap.height : bitmap.width
  canvas.height = swapSides ? bitmap.width : bitmap.height
  const outputMime = ['image/jpeg', 'image/png', 'image/webp'].includes(preferredMimeType)
    ? preferredMimeType
    : 'image/jpeg'
  const context = canvas.getContext('2d', { alpha: outputMime !== 'image/jpeg' })
  if (!context) throw new Error('Canvas 2D unavailable')
  context.translate(canvas.width / 2, canvas.height / 2)
  context.rotate((degrees * Math.PI) / 180)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2)
  bitmap.close()
  const rotated = await canvasToBlob(canvas, outputMime, 0.94)
  return processOnMainThread(rotated)
}

export async function processImage(file: Blob): Promise<ProcessedImage> {
  if (!file.type.startsWith('image/')) throw new Error('The selected file is not an image')
  if (typeof createImageBitmap === 'undefined') throw new Error('The browser does not support the required image decoding')

  if (canUseWorker()) {
    try {
      return await runWorker({ action: 'process', file })
    } catch {
      return processOnMainThread(file)
    }
  }
  return processOnMainThread(file)
}

export async function rotateImage(
  file: Blob,
  degrees: 90 | -90 | 180,
  preferredMimeType: string
): Promise<ProcessedImage> {
  if (canUseWorker()) {
    try {
      return await runWorker({ action: 'rotate', file, degrees, preferredMimeType })
    } catch {
      return rotateOnMainThread(file, degrees, preferredMimeType)
    }
  }
  return rotateOnMainThread(file, degrees, preferredMimeType)
}
